/**
 * CalendarWeekView
 * Weekly calendar view showing days of the week with events and tasks
 */

import React from 'react'
import type { CalendarItem } from '../../types/calendar'
import { CalendarEventCard } from './CalendarEventCard'
import { CalendarTaskCard } from './CalendarTaskCard'

interface CalendarWeekViewProps {
  items: CalendarItem[]
  weekStart: Date
  onItemClick?: (item: CalendarItem) => void
  onDateClick?: (date: Date) => void
}

interface DayColumn {
  date: Date
  dayNumber: number
  isToday: boolean
  items: CalendarItem[]
}

/**
 * CalendarWeekView Component
 */
export const CalendarWeekView: React.FC<CalendarWeekViewProps> = ({
  items,
  weekStart,
  onItemClick,
  onDateClick
}) => {
  // Generate 7 days starting from weekStart
  const generateWeekDays = (): DayColumn[] => {
    const days: DayColumn[] = []
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart)
      date.setDate(weekStart.getDate() + i)
      date.setHours(0, 0, 0, 0)

      days.push({
        date: new Date(date),
        dayNumber: date.getDate(),
        isToday: date.getTime() === today.getTime(),
        items: []
      })
    }

    return days
  }

  // Assign items to days
  const assignItemsToDays = (days: DayColumn[]): DayColumn[] => {
    items.forEach(item => {
      const itemDate = new Date(item.date)
      itemDate.setHours(0, 0, 0, 0)

      const day = days.find(d => d.date.getTime() === itemDate.getTime())
      if (day) {
        day.items.push(item)
      }
    })

    // Sort items within each day
    days.forEach(day => {
      day.items.sort((a, b) => {
        // Events with start time first
        if (a.type === 'event' && a.startTime && b.type === 'event' && b.startTime) {
          return a.startTime.localeCompare(b.startTime)
        }
        if (a.type === 'event' && a.startTime) return -1
        if (b.type === 'event' && b.startTime) return 1

        // Then tasks by priority
        const priorityOrder = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }
        return (priorityOrder[a.priority as keyof typeof priorityOrder] || 4) -
               (priorityOrder[b.priority as keyof typeof priorityOrder] || 4)
      })
    })

    return days
  }

  const weekDays = assignItemsToDays(generateWeekDays())

  const weekDayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

  return (
    <div className="min-w-0 overflow-x-auto rounded-lg border bg-card">
      <div className="min-w-[640px]">
      {/* Week Header */}
      <div className="grid grid-cols-7 border-b">
        {weekDays.map((day, index) => (
          <div
            key={index}
            className={`
              p-3 text-center border-r last:border-r-0
              ${day.isToday ? 'bg-accent/60' : ''}
            `}
          >
            <div className="text-xs text-muted-foreground font-medium">
              {weekDayNames[day.date.getDay()]}
            </div>
            <div className={`
              mt-1 text-2xl font-semibold tabular-nums
              ${day.isToday ? 'text-primary' : 'text-foreground'}
            `}>
              {day.dayNumber}
            </div>
          </div>
        ))}
      </div>

      {/* Week Content */}
      <div className="grid grid-cols-7 min-h-[500px]">
        {weekDays.map((day, index) => (
          <div
            key={index}
            className={`
              min-w-0 border-r last:border-r-0 p-2 cursor-pointer
              hover:bg-muted transition-colors
              ${day.isToday ? 'bg-accent/30' : ''}
            `}
            onClick={() => onDateClick?.(day.date)}
          >
            {/* Items */}
            <div className="space-y-1.5">
              {day.items.length === 0 && (
                <div className="py-4 text-center text-xs text-muted-foreground">
                  Sem eventos
                </div>
              )}

              {day.items.map((item) => (
                <div
                  key={`${item.type}-${item.id}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onItemClick?.(item)
                  }}
                >
                  {item.type === 'event' ? (
                    <CalendarEventCard item={item} />
                  ) : (
                    <CalendarTaskCard item={item} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  )
}
